import type { PowerBranding as PowerBrandingType } from '@src/lib/graphql/generated';
import { useFathomTracker } from '@src/lib/hooks/useFathomTracker';
import { Image, Link } from '@vgn-medien-holding/vgn-fe-components';
import type React from 'react';
import { useCallback, useEffect, useRef } from 'react';
import { useInView } from 'react-intersection-observer';

interface PowerBrandingProps {
  branding: PowerBrandingType;
}

export const PowerBranding = ({ branding }: PowerBrandingProps) => {
  const portraitVideoRef = useRef<HTMLVideoElement>(null);
  const landscapeVideoRef = useRef<HTMLVideoElement>(null);

  const { trackEvent } = useFathomTracker();

  const [trackingRef, isInView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const [portraitRef, isPortraitVisible] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const [landscapeRef, isLandscapeVisible] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  // Safari detection
  const isSafari = useCallback(() => {
    return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  }, []);

  const attemptPlay = useCallback(
    (videoElement: HTMLVideoElement | null) => {
      if (!videoElement) return;

      // Set data attribute to track loading state
      videoElement.setAttribute('data-loaded', 'true');

      const playPromise = videoElement.play();
      if (playPromise !== undefined) {
        playPromise.catch((error) => {
          console.error('Video failed to autoplay:', error);

          // For Safari, try again with user interaction simulation
          if (isSafari()) {
            document.addEventListener(
              'touchstart',
              () => {
                videoElement.play().catch((e) => console.error('Play after touch failed:', e));
              },
              { once: true },
            );
          }
        });
      }
    },
    [isSafari],
  );

  const handleVideoLoading = useCallback(
    (isVisible: boolean, videoRef: React.RefObject<HTMLVideoElement>) => {
      if (isVisible && videoRef.current) {
        const videoElement = videoRef.current;

        const handleCanPlay = () => {
          attemptPlay(videoElement);
        };

        // If already loaded enough, play immediately
        if (videoElement.readyState >= 3) {
          attemptPlay(videoElement);
        } else {
          // Otherwise wait for canplay event
          videoElement.addEventListener('canplay', handleCanPlay);
        }

        // For Safari, try to play immediately regardless of state
        if (isSafari()) {
          attemptPlay(videoElement);
        }

        return () => {
          videoElement.removeEventListener('canplay', handleCanPlay);
        };
      }
      return undefined;
    },
    [attemptPlay, isSafari],
  );

  useEffect(() => {
    if (isInView) {
      trackEvent('Power Branding View');
    }
  }, [isInView, trackEvent]);

  useEffect(() => {
    return handleVideoLoading(isPortraitVisible, portraitVideoRef);
  }, [handleVideoLoading, isPortraitVisible]);

  useEffect(() => {
    return handleVideoLoading(isLandscapeVisible, landscapeVideoRef);
  }, [handleVideoLoading, isLandscapeVisible]);

  if (!branding?.power_branding_image_portrait?.url) return null;

  return (
    <Link
      noLink={!branding?.power_branding_url}
      ref={trackingRef}
      onClick={branding?.power_branding_url ? () => trackEvent('Power Branding Click') : null}
      href={branding?.power_branding_url}
      classProps={{ root: 'relative block bg-background' }}
    >
      {branding?.power_branding_image_portrait?.mime.startsWith('video/') ? (
        <div
          ref={portraitRef}
          className="brand-portrait-tall:mt-[-10vh] brand-portrait-tall:mb-[-12vh] mb-[-10vh] brand-landscape:hidden brand-square:hidden brand-portrait-tall:aspect-[9/16] brand-portrait-tall:h-auto h-[80vh] min-h-[480px] w-full"
        >
          {isPortraitVisible && (
            <video
              ref={portraitVideoRef}
              muted
              playsInline
              loop
              autoPlay
              preload="metadata"
              poster={`${branding?.power_branding_image_portrait?.url}?width=1080&height=1920&q=60`}
              src={branding?.power_branding_image_portrait?.url}
              className="size-full object-cover"
            />
          )}
        </div>
      ) : (
        <Image
          src={branding?.power_branding_image_portrait?.url}
          alt={branding?.power_branding_image_portrait?.description}
          copyright={branding?.power_branding_image_portrait?.copyright_text}
          imageMode="cover"
          classProps={{
            container:
              'h-[80vh] w-full min-h-[480px] brand-landscape:hidden brand-square:hidden brand-portrait-tall:aspect-[9/16] brand-portrait-tall:mt-[-10vh] brand-portrait-tall:h-auto brand-portrait-tall:mb-[-12vh] mb-[-10vh]',
          }}
          fill
          sizes="100vw"
          loading="lazy"
        />
      )}

      {branding?.power_branding_image_landscape?.mime.startsWith('video/') ? (
        <div
          ref={landscapeRef}
          className="mb-[-15vh] brand-landscape:block brand-square:block hidden brand-landscape-wide:aspect-[21/9] brand-square:aspect-[12/9] brand-landscape-wide:h-auto brand-square:h-auto h-[85vh]"
        >
          {isLandscapeVisible && (
            <video
              ref={landscapeVideoRef}
              muted
              playsInline
              autoPlay
              loop
              preload="metadata"
              src={branding?.power_branding_image_landscape?.url}
              poster={`${branding?.power_branding_image_landscape?.url}?width=1920&height=1080&q=60`}
              className="size-full object-cover"
            />
          )}
        </div>
      ) : (
        <Image
          src={branding?.power_branding_image_landscape?.url}
          alt={branding?.power_branding_image_landscape?.description}
          copyright={branding?.power_branding_image_landscape?.copyright_text}
          imageMode="cover"
          classProps={{
            container:
              'h-[85vh] brand-landscape-wide:h-auto hidden brand-landscape:block brand-square:block brand-square:h-auto brand-square:aspect-[12/9] brand-landscape-wide:aspect-[21/9] mb-[-15vh]',
          }}
          fill
          sizes="100vw"
          loading="lazy"
        />
      )}
      <div className="absolute inset-x-0 bottom-0 z-10 h-[9vh] bg-gradient-to-t from-background via-background/90 to-background/0"></div>
    </Link>
  );
};
